import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { categoryAPI, Category } from '../services/api';

// Icon mapping - maps icon identifiers to emoji or icon names
export const CATEGORY_ICONS: Record<string, string> = {
  'food': '🍔',
  'transportation': '🚗',
  'entertainment': '🎬',
  'utilities': '💡',
  'healthcare': '🏥',
  'shopping': '🛍️',
  'education': '📚',
  'other': '📦',
  'housing': '🏠',
  'savings': '💰',
  'gaming': '🎮',
  'travel': '✈️',
  'dining': '🍕',
  'coffee': '☕',
  'gifts': '🎁',
  'payments': '💳',
  'groceries': '🛒',
  'insurance': '🛡️',
  'investments': '📈',
  'clothing': '👕',
  'personal': '👤',
  'pets': '🐾',
  'fitness': '💪',
  'beauty': '💄',
  'subscriptions': '📱',
  'electronics': '💻',
  'maintenance': '🔧',
  'taxes': '📋',
  'charity': '❤️',
  'business': '💼',
};

// Helper function to get icon for a category
// The API stores icon names (e.g., "food", "transportation"), this function maps them to emojis
export const getCategoryIcon = (category: Category | string): string => {
  if (typeof category === 'string') {
    // Look up the icon name in the mapping
    return CATEGORY_ICONS[category.toLowerCase()] || '📦';
  }
  // If it's a Category object, use the icon field
  if (category.icon) {
    // Look up the icon name in the mapping
    return CATEGORY_ICONS[category.icon.toLowerCase()] || '📦';
  }
  // Fall back to mapping by category name
  return CATEGORY_ICONS[category.name.toLowerCase()] || '📦';
};

interface CategoryContextType {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  getCategoryByName: (name: string) => Category | undefined;
  getCategoryById: (id: string) => Category | undefined;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

interface CategoryProviderProps {
  children: ReactNode;
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryAPI.getAll();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getCategoryByName = useCallback((name: string): Category | undefined => {
    return categories.find(cat => cat.name.toLowerCase() === name.toLowerCase());
  }, [categories]);

  const getCategoryById = useCallback((id: string): Category | undefined => {
    return categories.find(cat => cat.id === id);
  }, [categories]);

  const value: CategoryContextType = {
    categories,
    loading,
    error,
    refreshCategories: fetchCategories,
    getCategoryByName,
    getCategoryById,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = (): CategoryContextType => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

